import { Dispatch, SetStateAction, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
// @mui
import { Fab, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { trackEvent, usePageView } from 'src/analytics';
import { FOOTER_HEIGHT, HEADER_HEIGHT, MOBILE_MAX_WIDTH } from 'src/assets/static/constants';
import AppMessageListener from 'src/components/AppMessageListener';
import { LoadingScreen } from 'src/components/loading-screen';
import ScrollToTop from 'src/components/scroll-to-top';
import { useStores } from 'src/models';
import { PATH_PAGE } from 'src/routes/paths';
import { isMockAuthenticated } from 'src/services/oauth/mock-auth';

import { Footer } from '../footer';
import { Header } from '../header';
import { HeaderProps } from '../header/Header';

// ----------------------------------------------------------------------

export interface LayoutProps {
  setHeaderProps: Dispatch<SetStateAction<HeaderProps>>;
}

interface MobileLayoutProps {
  needHeader?: boolean;
  needFooter?: boolean;
  needFloating?: boolean;
}

interface FloatingButtonProps {
  needFooter?: boolean;
}

function MobileLayout({
  needHeader = true,
  needFooter = true,
  needFloating = false,
}: MobileLayoutProps) {
  const { authStore } = useStores();
  usePageView();

  const [headerProps, setHeaderProps] = useState<HeaderProps>({});

  // mock 인증 사용자도 footer 노출 (BE 미연결 상태 우회)
  const showFooter = needFooter && (!!authStore?.user || isMockAuthenticated());

  return (
    <Container needHeader={needHeader}>
      {needHeader && <Header {...headerProps} />}
      <MainContainer needHeader={needHeader} needFooter={showFooter}>
        <ContentColumn>
          <Outlet context={{ setHeaderProps } as LayoutProps} />
        </ContentColumn>
      </MainContainer>
      {showFooter && <Footer />}
      {needFloating && <FloatingButton needFooter={showFooter} />}
      <ScrollToTop />
      <LoadingScreen />
      <AppMessageListener />
    </Container>
  );
}

// 데스크탑에서 헤더 바·배경이 화면 전체로 늘어나도록 maxWidth 제거 (풀폭 캔버스).
// 실제 콘텐츠는 아래 ContentColumn이 MOBILE_MAX_WIDTH로 가운데 정렬한다.
const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'needHeader',
})<{ needHeader?: boolean }>(({ needHeader = true }) => ({
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  // 배경 그라데이션을 스크롤 컨테이너(=뷰포트 높이 100vh)에 고정.
  // 콘텐츠가 길어져도 그라데이션은 늘어나지 않고 항상 동일하게 표시된다.
  // 헤더 화면(흰 배경)은 MainContainer가 흰색을 덮으므로 단색으로 둔다.
  background: needHeader ? '#FBF8F4' : 'linear-gradient(180deg, #E5EDE2 0%, #FBF8F4 100%)',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
}));

// 콘텐츠 가운데 정렬 칼럼 (모바일 폭 유지). 헤더 바/배경은 풀폭, 콘텐츠는 768.
const ContentColumn = styled('div')({
  width: '100%',
  maxWidth: MOBILE_MAX_WIDTH,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
});

const MainContainer = styled('main', {
  shouldForwardProp: (prop) => prop !== 'needFooter' && prop !== 'needHeader',
})<{ needFooter?: boolean; needHeader?: boolean }>(({ needFooter = true, needHeader = true }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: needHeader ? HEADER_HEIGHT : 0,
  paddingBottom: needFooter ? FOOTER_HEIGHT : 0,
  // 헤더 화면은 흰색 배경, 헤더 없는 화면(5탭 메인)은 투명 처리하여
  // Container의 뷰포트-고정 그라데이션이 그대로 비치도록 한다.
  background: needHeader ? '#FFFFFF' : 'transparent',
}));

export default observer(MobileLayout);

const FloatingButton = ({ needFooter }: FloatingButtonProps) => {
  const navigate = useNavigate();
  const { authStore } = useStores();

  // W2: 챗봇 풀스크린 진입 FAB로 교체 예정
  // 현재는 placeholder — 클릭 시 홈으로 이동
  return (
    authStore?.user && (
      <Fab
        sx={{
          backgroundColor: 'primary.main',
          position: 'fixed',
          bottom: needFooter ? FOOTER_HEIGHT + 20 : 20,
          right: 20,
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
        onClick={() => {
          trackEvent('notification_view');
          navigate(PATH_PAGE.home);
        }}
      >
        <Typography
          sx={{
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: '18px',
            letterSpacing: '1px',
          }}
        >
          💬
        </Typography>
      </Fab>
    )
  );
};
